import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { useSchedules } from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { readOnlyReason } from '../lib/dryrun'
import type { Schedule, ScheduleBody, ScheduleKind } from '../api/types'
import { SelectField } from './Directory'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** The week grid keys (see access/models.py validate_week) in display order. */
const WEEK_DAYS = [
  { key: 'mon', label: 'Monday', short: 'Mon' },
  { key: 'tue', label: 'Tuesday', short: 'Tue' },
  { key: 'wed', label: 'Wednesday', short: 'Wed' },
  { key: 'thu', label: 'Thursday', short: 'Thu' },
  { key: 'fri', label: 'Friday', short: 'Fri' },
  { key: 'sat', label: 'Saturday', short: 'Sat' },
  { key: 'sun', label: 'Sunday', short: 'Sun' },
  { key: 'holidays', label: 'Holidays', short: 'Hol' },
] as const

const timeClass =
  'tnum rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold'

/** "Mon 07:00–18:00 · Hol 09:00–12:00 · 3 holiday dates" — 'No hours defined' when empty. */
function scheduleSummary(s: Schedule): string {
  const parts: string[] = []
  for (const d of WEEK_DAYS) {
    const intervals = s.week[d.key]
    if (intervals && intervals.length > 0) {
      parts.push(`${d.short} ${intervals.map(([a, b]) => `${a}–${b}`).join(', ')}`)
    }
  }
  if (s.holiday_dates.length > 0) {
    parts.push(`${s.holiday_dates.length} holiday date${s.holiday_dates.length === 1 ? '' : 's'}`)
  }
  return parts.length > 0 ? parts.join(' · ') : 'No hours defined'
}

/** One weekday's list of [start, end] ranges with add/remove. */
function DayEditor({
  label,
  intervals,
  onChange,
}: {
  label: string
  intervals: string[][]
  onChange: (intervals: string[][]) => void
}) {
  const setAt = (i: number, which: 0 | 1, value: string) =>
    onChange(intervals.map((iv, j) => (j === i ? (which === 0 ? [value, iv[1]] : [iv[0], value]) : iv)))
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        {label}
      </span>
      {intervals.map(([start, end], i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="time"
            aria-label={`${label} range ${i + 1} start`}
            value={start}
            onChange={(e) => setAt(i, 0, e.target.value)}
            className={timeClass}
          />
          <span className="text-xs text-ostrel-ink-muted">–</span>
          <input
            type="time"
            aria-label={`${label} range ${i + 1} end`}
            value={end}
            onChange={(e) => setAt(i, 1, e.target.value)}
            className={timeClass}
          />
          <button
            type="button"
            aria-label={`Remove ${label} range ${i + 1}`}
            onClick={() => onChange(intervals.filter((_, j) => j !== i))}
            className="text-ostrel-ink-muted transition-colors hover:text-ostrel-danger focus:outline-none"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...intervals, ['07:00', '18:00']])}
        className="self-start text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold transition-colors hover:text-ostrel-accent focus:outline-none"
      >
        + Add range
      </button>
    </div>
  )
}

/** Create/edit form: name + weekly grid + holiday dates. */
function ScheduleFormModal({
  open,
  schedule,
  onClose,
}: {
  open: boolean
  /** When set, edits this schedule; otherwise creates a new one. */
  schedule: Schedule | null
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [week, setWeek] = useState<Record<string, string[][]>>({})
  const [holidays, setHolidays] = useState<string[]>([])
  const [newHoliday, setNewHoliday] = useState('')

  useEffect(() => {
    if (!open) return
    setName(schedule?.name ?? '')
    // Deep-copy the grid so editing never mutates the cached query data.
    setWeek(Object.fromEntries(Object.entries(schedule?.week ?? {}).map(([k, v]) => [k, v.map((iv) => [...iv])])))
    setHolidays([...(schedule?.holiday_dates ?? [])])
    setNewHoliday('')
    // Only (re)initialize when the modal opens or the target schedule changes.
  }, [open, schedule])

  const save = useMutation({
    mutationFn: () => {
      // Drop empty days; the backend validates HH:MM and start < end.
      const cleanWeek = Object.fromEntries(
        Object.entries(week).filter(([, intervals]) => intervals.length > 0),
      )
      const body: ScheduleBody = { name: name.trim(), week: cleanWeek, holiday_dates: holidays }
      if (schedule) {
        // Editing keeps the schedule's kind (a vendor-read-only alarm/reservation
        // schedule stays one). Create sends no kind on purpose: console-created
        // schedules are always door schedules, and the server defaults kind to
        // 'door' — there is deliberately no kind picker here.
        body.kind = schedule.kind
      }
      return schedule
        ? api.updateSchedule(schedule.unique_id, body)
        : api.createSchedule(body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'schedules'] })
      onClose()
    },
  })

  const invalidRange = Object.values(week).some((intervals) =>
    intervals.some(([start, end]) => !start || !end || start >= end),
  )

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (name.trim() && !invalidRange) save.mutate()
  }

  return (
    <Modal open={open} title={schedule ? 'Edit schedule' : 'New schedule'} onClose={onClose}>
      <form onSubmit={submit} className="flex max-h-[75vh] flex-col gap-4 overflow-y-auto pr-1">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Office hours"
          autoFocus
        />
        <div className="flex flex-col gap-4">
          {WEEK_DAYS.map((d) => (
            <DayEditor
              key={d.key}
              label={d.label}
              intervals={week[d.key] ?? []}
              onChange={(intervals) => setWeek((prev) => ({ ...prev, [d.key]: intervals }))}
            />
          ))}
        </div>
        {invalidRange && (
          <p className="text-xs text-ostrel-danger">Every range needs a start before its end.</p>
        )}

        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Holiday dates
          </span>
          {holidays.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {holidays.map((d) => (
                <li
                  key={d}
                  className="tnum flex items-center gap-1.5 rounded-sm border border-ostrel-line bg-ostrel-sand px-2 py-1 text-xs text-ostrel-ink"
                >
                  {d}
                  <button
                    type="button"
                    aria-label={`Remove ${d}`}
                    onClick={() => setHolidays((prev) => prev.filter((h) => h !== d))}
                    className="text-ostrel-ink-muted transition-colors hover:text-ostrel-danger focus:outline-none"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2">
            <input
              type="date"
              aria-label="New holiday date"
              value={newHoliday}
              onChange={(e) => setNewHoliday(e.target.value)}
              className={timeClass}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!newHoliday || holidays.includes(newHoliday)}
              onClick={() => {
                setHolidays((prev) => [...prev, newHoliday].sort())
                setNewHoliday('')
              }}
            >
              + Add date
            </Button>
          </div>
          <p className="text-[11px] text-ostrel-ink-muted">
            On these dates the Holidays ranges above apply instead of the weekday grid.
          </p>
        </div>

        {save.isError && <p className="text-xs text-ostrel-danger">{errText(save.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="navy" size="sm" disabled={save.isPending || !name.trim() || invalidRange}>
            {save.isPending ? 'Saving…' : schedule ? 'Save changes' : 'Create schedule'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function SchedulesPage() {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const [kindFilter, setKindFilter] = useState<ScheduleKind | 'all'>('all')
  const schedules = useSchedules(kindFilter === 'all' ? undefined : kindFilter)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Schedule | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Schedule | null>(null)

  const remove = useMutation({
    mutationFn: api.deleteSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'schedules'] })
      setConfirmDelete(null)
    },
  })

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (s: Schedule) => {
    setEditing(s)
    setFormOpen(true)
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Access"
        title="Schedules"
        action={<Button onClick={openCreate}>+ New schedule</Button>}
      />

      <div className="w-56">
        <SelectField
          label="Kind"
          value={kindFilter}
          onChange={(v) => setKindFilter(v as ScheduleKind | 'all')}
          options={[
            { value: 'all', label: 'All kinds' },
            { value: 'door', label: 'Door' },
            { value: 'alarm', label: 'Alarm' },
            { value: 'reservation', label: 'Reservation' },
          ]}
        />
      </div>

      {schedules.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : schedules.isError ? (
        <EmptyState title="Could not load schedules" hint={errText(schedules.error)} />
      ) : schedules.data.length === 0 ? (
        <EmptyState
          title={kindFilter === 'all' ? 'No schedules yet' : `No ${kindFilter} schedules`}
          hint="Schedules limit when door policies and door codes apply."
          action={
            <Button size="sm" onClick={openCreate}>
              + New schedule
            </Button>
          }
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Schedule</TH>
              <TH>Kind</TH>
              <TH>Hours</TH>
              <TH className="text-right">Action</TH>
            </tr>
          </THead>
          <TBody>
            {schedules.data.map((s) => {
              // A vendor-bound schedule on a dry-run system is read-only:
              // edits to an unmanaged binding are overwritten by the next
              // sync, and a managed one can't push -- either way the edit
              // would be a lie.
              const readOnlyHint = readOnlyReason(s)
              return (
              <TR key={s.unique_id}>
                <TD>
                  <span className="block text-sm font-medium text-ostrel-ink">{s.name}</span>
                  <span className="tnum block font-mono text-[10px] text-ostrel-ink-muted">
                    {s.unique_id}
                  </span>
                </TD>
                <TD>
                  {/* Door is the everyday kind; alarm/reservation are vendor-read-only
                      and get muted chips to signal that. */}
                  <Chip tone={s.kind === 'door' ? 'info' : 'muted'} label={s.kind} />
                </TD>
                <TD>
                  <span className="tnum text-xs text-ostrel-ink-muted">{scheduleSummary(s)}</span>
                </TD>
                <TD className="text-right">
                  <span className="flex items-center justify-end gap-2">
                    {readOnlyHint && (
                      <span className="text-[10px] text-ostrel-ink-muted">{readOnlyHint}</span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)} disabled={readOnlyHint !== undefined} title={readOnlyHint}>
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => setConfirmDelete(s)} disabled={readOnlyHint !== undefined} title={readOnlyHint}>
                      Delete
                    </Button>
                  </span>
                </TD>
              </TR>
              )
            })}
          </TBody>
        </Table>
      )}

      <ScheduleFormModal open={formOpen} schedule={editing} onClose={() => setFormOpen(false)} />

      <ConfirmDeleteModal
        open={confirmDelete !== null}
        title="Delete schedule"
        objectName={confirmDelete?.name ?? ''}
        expected={confirmDelete?.unique_id ?? ''}
        pending={remove.isPending}
        error={remove.isError ? errText(remove.error) : null}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.unique_id)}
        onClose={() => setConfirmDelete(null)}
      >
        <p>
          Delete <span className="font-medium">{confirmDelete?.name}</span>? Door policies and door
          codes referencing it must be updated first — the backend refuses while it is in use.
        </p>
      </ConfirmDeleteModal>
    </div>
  )
}
